import { Button, Flex, Icon, type IconName, Menu } from "@theorgchartdev/storybook";
import { appstr } from "@coreapp/utils/appstr";
import { enumRulePairLogicCondition } from "@coreapp/CoreApp/QRulePair";

export type ConditionOption = {
  value: (typeof enumRulePairLogicCondition)[keyof typeof enumRulePairLogicCondition];
};

type ConditionListItemOption = {
  labelKey: "and" | "or";
  icon: IconName;
} & ConditionOption;

const options: ConditionListItemOption[] = [
  { labelKey: "and", icon: "merge", value: enumRulePairLogicCondition.and },
  { labelKey: "or", icon: "split", value: enumRulePairLogicCondition.or }
];

const isConditionOption = (option: any): option is ConditionOption => {
  return Object.values(enumRulePairLogicCondition).includes(option?.value);
};

type Props = {
  isEditing: boolean;
  value: string;
  onChange?: (option: ConditionOption) => void;
  dataTestId?: string;
};

export const ConditionInput = (props: Props) => {
  const { isEditing, value, onChange, dataTestId } = props;

  const handleChange = (option: unknown) => {
    if (!isConditionOption(option)) {
      return;
    }

    onChange?.(option);
  };

  let selectedOption = options.find(option => option.value === value);
  if (!selectedOption) {
    selectedOption = options[0];
  }

  const triggerNode = (
    <Flex alignItems={"center"} gap={"8px"} padding={"0 12px 0 0"}>
      <Icon name={selectedOption.icon} size={"small"} />
      {appstr(selectedOption.labelKey)}
    </Flex>
  );

  if (!isEditing) {
    return triggerNode;
  }

  return (
    <Menu
      trigger={
        <Button
          modifier={"secondary"}
          size={"small"}
          iconAlignEnd
          iconProps={{ name: "chevron-down", size: "xsmall" }}
          dataTestId={dataTestId}
        >
          {triggerNode}
        </Button>
      }
      listProps={{ onSelect: handleChange }}
      options={options.map(({ labelKey, icon, value }) => ({
        label: appstr(labelKey),
        icon,
        value
      }))}
      value={value}
    />
  );
};
